<script lang="ts">
  export * from './Radio.types';

  export interface RadioProps {
    /**
     * Passed to the "name" attribute of the `<input>` element.
     */
    name?: string;

    /**
     * Whether the entire group should be disabled
     */
    disabled?: boolean;

    /**
     * Whether the group should expand to the parent's width
     */
    fullWidth?: boolean;

    /**
     * A unique string to distinguish one Radio instance from another
     * and to link the label to the input; required for accessibility
     */
    id: string | number;

    /**
     * value bound to the parent component
     */
    modelValue?: string | undefined;

    /**
     * Adds error styling
     */
    hasError?: boolean;

    /**
     * The description which appears to the right of the radio button
     */
    label?: string;

    /**
     * the styling for the radio button @defaultValue 'radio'
     */
    variant?: RadioVariants;

    /**
     * The value for the radio input.
     */
    value: string;
  }
</script>

<script setup lang="ts">
  import { computed, inject } from 'vue';

  import { RADIO_GROUP_INJECTION } from '../RadioGroup/RadioGroup.keys';
  import VariantButton from './components/VariantButton.vue';
  import VariantChip from './components/VariantChip.vue';
  import VariantRadio from './components/VariantRadio.vue';
  import VariantTile from './components/VariantTile.vue';
  import type { RadioVariants } from './Radio.types';
  import { RadioVariant } from './Radio.types';

  const variantComponentsMap = {
    [RadioVariant.Button]: VariantButton,
    [RadioVariant.Chip]: VariantChip,
    [RadioVariant.Radio]: VariantRadio,
    [RadioVariant.Tile]: VariantTile,
  };

  const radioGroupInjection = inject(RADIO_GROUP_INJECTION.key, null);

  const props = withDefaults(defineProps<RadioProps>(), {
    disabled: false,
    fullWidth: false,
    hasError: false,
    label: '',
    modelValue: undefined,
    name: undefined,
    variant: undefined,
  });

  /**
   * use prop values but fallback to radio group context values if they are provided
   */
  const derivedModelValue = computed(() => props.modelValue || radioGroupInjection?.modelValue.value);
  const derivedName = computed(() => props.name || radioGroupInjection?.name.value);
  const derivedNameFullWidth = computed(() => props.fullWidth || radioGroupInjection?.fullWidth.value);
  const derivedDisabled = computed(() => props.disabled || radioGroupInjection?.disabled.value);
  const derivedVariant = computed<RadioVariants>(() => props.variant || radioGroupInjection?.variant.value || 'radio');

  const emit = defineEmits<{
    (e: 'update:modelValue', value: RadioProps['value']): void;
  }>();

  function onInput(e: Event) {
    if (radioGroupInjection?.update) {
      radioGroupInjection.update(e);

      return;
    }

    emit('update:modelValue', (e.target as HTMLInputElement).value);
  }
</script>

<template>
  <component
    :is="variantComponentsMap[derivedVariant]"
    :id="props.id"
    :disabled="derivedDisabled"
    :has-error="props.hasError"
    :full-width="derivedNameFullWidth"
    :label="props.label"
    :model-value="derivedModelValue"
    :name="derivedName"
    :value="props.value"
    @input="onInput"
  >
    <slot></slot>
  </component>
</template>
